import { Box, Input, inputClasses, styled, useTheme } from '@mui/material';
import { NavPopper, NavSelect } from '@tymely/components';
import { AppMode, useAppMode, useOrganizationsQuery, useSelectedOrganizations } from '@tymely/services';
import React, { memo, RefObject, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchTickets } from '@tymely/api';
import { useKeys } from '@tymely/utils';

import LabeledSwitch from './LabeledSwitch';

const SearchPopper = styled(NavPopper)(
    ({ theme }) => `
    padding: 0;
    height: 66px;
    background: ${theme.nav.active};
`,
);
const SearchContainer = styled(Box)`
    display: flex;
    align-items: center;
    height: 100%;
    background: inherit;
    border-radius: inherit;
`;
const SearchInput = styled(Input)(
    ({ theme }) => `
    width: 35%;
    .${inputClasses.input} {
        width: 100%;
        background: ${theme.nav.active};
        outline: none;
        border: none;
        color: ${theme.palette.common.white};
        &::placeholder {
            color: #A7A9C1;
        }
    }
`,
);

const TicketSearch = memo((props: { anchorEl: HTMLElement | null; open: boolean; onClose: () => void }) => {
    const theme = useTheme();
    const { data: orgs } = useOrganizationsQuery();
    const selectedOrgs = useSelectedOrganizations();
    const [org, setSelectedOrg] = useState<number | undefined>();
    const [external, setExt] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { appMode, navigateTicket } = useAppMode();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (props.open) {
            inputRef.current?.focus();
        }
    }, [props.open]);
    const options = React.useMemo(() => {
        return (orgs || selectedOrgs)?.map((org) => ({ label: org.name, value: org.id }));
    }, [orgs]);

    const onSwitch = React.useCallback(() => {
        setSelectedOrg(undefined);
        setExt((prev) => !prev);
    }, []);

    const onSearch = React.useCallback(
        (ticketId: string) => {
            navigateTicket(ticketId, appMode === AppMode.Online ? AppMode.Training : appMode);
            props.onClose();
        },
        [navigateTicket, navigate, props.onClose, appMode],
    );

    const searchTickets = useSearchTickets();

    const controller = React.useRef(new AbortController());
    useKeys(
        ['Enter'],
        () => {
            if (!props.open) return;
            controller.current.abort();
            controller.current = new AbortController();
            const ticketId = inputRef.current?.value || '';
            if (org) {
                return searchTickets(
                    {
                        query: ticketId.trim(),
                        org,
                        maxResults: 1,
                    },
                    controller.current.signal,
                ).then((results) => {
                    if (results.length) {
                        onSearch(String(results[0].id));
                    }
                });
            }
            if (!external) {
                return onSearch(ticketId);
            }
        },
        { ref: inputRef as RefObject<HTMLInputElement> },
    );

    return (
        <SearchPopper anchorEl={props.anchorEl} open={props.open}>
            <SearchContainer>
                <SearchInput inputRef={inputRef} placeholder="Search Ticket ID" />
                <NavSelect
                    sx={{ opacity: external ? 1 : 0 }}
                    value={org}
                    options={props.open ? options : undefined}
                    onSelect={setSelectedOrg}
                />
                <LabeledSwitch
                    checked={external}
                    sx={{ ml: 2, mr: 2, fontSize: theme.typography.fontSmall }}
                    onLabel="Ext"
                    offLabel="Int"
                    onChange={onSwitch}
                />
            </SearchContainer>
        </SearchPopper>
    );
});

TicketSearch.displayName = 'TicketSearch';

export default TicketSearch;
